import { FC, useState, useEffect } from 'react'
import { ApiService, AnalyticsKPIs, SettlementPerformanceData, RiskMetrics, ApiMetrics } from '../../services/api'
import toast from 'react-hot-toast'
import { getBackendUrl } from '../../utils/backendUrl'
import { FeatureNotAvailablePopup, useFeatureNotAvailable } from '../../components/FeatureNotAvailable'
import soc2 from '../../assets/images/soc2.svg'
import pci from '../../assets/images/pci.svg'
import iso from '../../assets/images/iso.svg'
import gdpr from '../../assets/images/gdpr.svg'

const AdminAnalytics: FC = () => {
  const [kpis, setKpis] = useState<AnalyticsKPIs | null>(null)
  const [settlementPerformance, setSettlementPerformance] = useState<SettlementPerformanceData[]>([])
  const [riskMetrics, setRiskMetrics] = useState<RiskMetrics | null>(null)
  const [apiMetrics, setApiMetrics] = useState<ApiMetrics | null>(null)
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d'>('24h')
  const [loading, setLoading] = useState(true)
  const [isPaused, setIsPaused] = useState(false)
  const [approvalWorkflowsEnabled, setApprovalWorkflowsEnabled] = useState(false)
  const [showApprovalWorkflows, setShowApprovalWorkflows] = useState(false)
  const [showWorkflowDialog, setShowWorkflowDialog] = useState(false)
  const [showLimitsDialog, setShowLimitsDialog] = useState(false)
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [exportForm, setExportForm] = useState({
    startDate: '',
    startTime: '',
    endDate: '',
    endTime: '',
    exportType: 'pdf' as 'pdf' | 'csv'
  })
  const [isExporting, setIsExporting] = useState(false)
  
  // Workflow configuration state
  const [workflowConfig, setWorkflowConfig] = useState({
    under50k: 'Auto-approve',
    under500k: '1 approval',
    over500k: '2 approvals'
  })
  
  // Custom limits state
  const [customLimits, setCustomLimits] = useState({
    daily: 10000000, // $10M in cents
    perTransaction: 5000000, // $5M in cents
    velocity: 100 // per hour
  })

  // Feature not available popup
  const featurePopup = useFeatureNotAvailable()

  useEffect(() => {
    fetchAnalyticsData()
    fetchSettlementStatus()
  }, [timeRange])

  const fetchSettlementStatus = async () => {
    try {
      const status = await ApiService.getSettlementStatus()
      setIsPaused(status.isPaused)
    } catch (error) {
      console.error('Error fetching settlement status:', error)
    }
  }

  const fetchAnalyticsData = async () => {
    try {
      setLoading(true)
      const [kpisData, performanceData, riskData, apiData] = await Promise.all([
        ApiService.getAnalyticsKPIs(timeRange),
        ApiService.getSettlementPerformance(timeRange),
        ApiService.getRiskMetrics(),
        ApiService.getApiMetrics()
      ])
      
      setKpis(kpisData)
      setSettlementPerformance(performanceData)
      setRiskMetrics(riskData)
      setApiMetrics(apiData)
    } catch (error) {
      console.error('Error fetching analytics data:', error)
      toast.error('Failed to load analytics data')
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num)
  }

  const formatPercentage = (num: number) => {
    return `${num.toFixed(1)}%`
  }

  const getMaxHeight = (data: SettlementPerformanceData[]) => {
    if (data.length === 0) return 0
    return Math.max(...data.map(d => d.volume))
  }

  const handleApprovalWorkflowsToggle = () => {
    setApprovalWorkflowsEnabled(!approvalWorkflowsEnabled)
    setShowApprovalWorkflows(!approvalWorkflowsEnabled)
  }

  const handlePauseSettlements = async () => {
    try {
      if (isPaused) {
        // Resume settlements
        await ApiService.resumeSettlements()
        setIsPaused(false)
        toast.success('Settlements resumed successfully')
      } else {
        // Pause settlements
        await ApiService.pauseSettlements()
        setIsPaused(true)
        toast.success('Settlements paused - All settlement requests will be rejected')
      }
    } catch (error) {
      console.error('Error toggling settlements:', error)
      toast.error('Failed to toggle settlements status')
    }
  }

  const handleSaveWorkflowConfig = () => {
    // Here you would typically save to backend
    toast.success('Workflow configuration saved successfully')
    setShowWorkflowDialog(false)
  }

  const handleSaveLimitsConfig = () => {
    // Here you would typically save to backend
    toast.success('Custom limits updated successfully')
    setShowLimitsDialog(false)
  }

  const handleExportTransactionHistory = async () => {
    try {
      setIsExporting(true)
      
      // Validate form
      if (!exportForm.startDate || !exportForm.endDate) {
        toast.error('Please select both start and end dates')
        return
      }

      // Create datetime strings
      const startDateTime = `${exportForm.startDate}T${exportForm.startTime || '00:00'}:00.000Z`
      const endDateTime = `${exportForm.endDate}T${exportForm.endTime || '23:59'}:59.999Z`

      if (exportForm.exportType === 'csv') {
        // Handle CSV export (backend generates the file)
        const response = await ApiService.exportTransactionHistory({
          startDate: startDateTime,
          endDate: endDateTime,
          exportType: exportForm.exportType
        })

        // Create download link
        const blob = new Blob([response.data], { type: 'text/csv' })
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `transaction-history-${exportForm.startDate}-to-${exportForm.endDate}.csv`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)
      } else {
        // Handle PDF export (frontend generates the file)
        const response = await ApiService.exportTransactionHistory({
          startDate: startDateTime,
          endDate: endDateTime,
          exportType: exportForm.exportType
        })

        if (response.success && response.data) {
          await generatePDFFromData(response.data)
        } else {
          throw new Error('Failed to get transaction data')
        }
      }

      toast.success('Transaction history exported successfully')
      setShowExportDialog(false)
    } catch (error) {
      console.error('Error exporting transaction history:', error)
      toast.error('Failed to export transaction history')
    } finally {
      setIsExporting(false)
    }
  }

  const generatePDFFromData = (data: any) => {
    const filename = `transaction-history-${exportForm.startDate}-to-${exportForm.endDate}.pdf`;
    
    // Open PDF page in new tab
    const pdfUrl = '/pdf/transaction-history';
    const newWindow = window.open(pdfUrl, '_blank');
    
    if (newWindow) {
      // Wait for the new window to load, then pass the data
      newWindow.addEventListener('load', () => {
        newWindow.postMessage({
          type: 'PDF_DATA',
          data: data,
          filename: filename
        }, window.location.origin);
      });
    } else {
      // Fallback if popup is blocked
      toast.error('Please allow popups for this site to generate PDF');
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount / 100) // Convert from cents
  }

  if (loading) {
    return (
      <div className="relative flex h-auto min-h-screen w-full flex-col">
        <main className="flex-1 p-4">
          <div className="flex items-center justify-center h-64">
            <div className="text-text-light-primary dark:text-text-dark-primary">Loading analytics...</div>
          </div>
        </main>
      </div>
    )
  }
  return (
    <div className="relative flex h-auto min-h-screen w-full max-w-7xl mx-auto flex-col">
      <main className="flex-1 p-4">
        <div className="flex flex-col gap-6">
          {/* Key Performance Indicators Section */}
          <section>
            <h2 className="px-2 pb-3 text-xl font-bold text-text-light-primary dark:text-text-dark-primary">Key Performance Indicators</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              <div className="flex flex-col gap-2 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <p className="text-sm font-medium text-text-light-secondary dark:text-text-dark-secondary">Settlements</p>
                <p className="text-2xl font-bold tracking-tight text-text-light-primary dark:text-text-dark-primary">
                  {kpis ? formatNumber(kpis.settlements) : '0'}
                </p>
                <p className="text-sm font-medium text-success text-green-500">+5.2%</p>
              </div>
              <div className="flex flex-col gap-2 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <p className="text-sm font-medium text-text-light-secondary dark:text-text-dark-secondary">Avg. Time</p>
                <p className="text-2xl font-bold tracking-tight text-text-light-primary dark:text-text-dark-primary">
                  {kpis ? `${kpis.avgTime}s` : '0s'}
                </p>
                <p className="text-sm font-medium text-critical">-1.1%</p>
              </div>
              <div className="flex flex-col gap-2 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <p className="text-sm font-medium text-text-light-secondary dark:text-text-dark-secondary">Total Volume</p>
                <p className="text-2xl font-bold tracking-tight text-text-light-primary dark:text-text-dark-primary">
                  {kpis ? formatCurrency(kpis.totalVolume) : '$0'}
                </p>
                <p className="text-sm font-medium text-success text-green-500">+8.0%</p>
              </div>
              <div className="flex flex-col gap-2 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <p className="text-sm font-medium text-text-light-secondary dark:text-text-dark-secondary">Success Rate</p>
                <p className="text-2xl font-bold tracking-tight text-text-light-primary dark:text-text-dark-primary">
                  {kpis ? formatPercentage(kpis.successRate) : '0%'}
                </p>
                <p className="text-sm font-medium text-success text-green-500">+0.1%</p>
              </div>
              <div className="flex flex-col gap-2 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <p className="text-sm font-medium text-text-light-secondary dark:text-text-dark-secondary">Fees Saved</p>
                <p className="text-2xl font-bold tracking-tight text-text-light-primary dark:text-text-dark-primary">
                  {kpis ? formatCurrency(kpis.feesSaved) : '$0'}
                </p>
                <p className="text-sm font-medium text-success text-green-500">+3.5%</p>
              </div>
              <div className="flex flex-col gap-2 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <p className="text-sm font-medium text-text-light-secondary dark:text-text-dark-secondary">Participants</p>
                <p className="text-2xl font-bold tracking-tight text-text-light-primary dark:text-text-dark-primary">
                  {kpis ? formatNumber(kpis.participants) : '0'}
                </p>
                <p className="text-sm font-medium text-success text-green-500">+2.0%</p>
              </div>
            </div>
          </section>

          {/* Settlement Performance Charts */}
          <section>
            <h2 className="px-2 pb-3 text-xl font-bold text-text-light-primary dark:text-text-dark-primary">Settlement Performance</h2>
            <div className="rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
              <div className="flex items-center justify-between pb-4">
                <div>
                  <h3 className="font-bold text-text-light-primary dark:text-text-dark-primary">Settlement Velocity</h3>
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">Volume & Time Distribution</p>
                </div>
                <div className="flex gap-2">
                  {(['24h', '7d', '30d', '90d'] as const).map((range) => (
                    <button
                      key={range}
                      onClick={() => setTimeRange(range)}
                      className={`text-sm font-medium px-3 py-1 rounded ${
                        timeRange === range
                          ? 'bg-primary text-white'
                          : 'text-primary hover:bg-primary/10'
                      }`}
                    >
                      {range}
                    </button>
                  ))}
                </div>
              </div>
              <div className="h-48 flex items-end gap-2">
                {settlementPerformance.length > 0 ? (
                  settlementPerformance.map((data, index) => {
                    const maxHeight = getMaxHeight(settlementPerformance)
                    const heightPercentage = maxHeight > 0 ? (data.volume / maxHeight) * 100 : 0
                    return (
                      <div
                        key={index}
                        className="flex-1 rounded-t bg-[#2777EF] hover:bg-[#2777EF]/30 transition-colors cursor-pointer group relative"
                        style={{ height: `${Math.max(heightPercentage, 5)}%` }}
                        title={`${data.period}: ${formatCurrency(data.volume)} (${data.count} transactions)`}
                      >
                        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
                          {data.period}
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="flex-1 flex items-center justify-center text-text-light-secondary dark:text-text-dark-secondary">
                    No data available
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* Risk Management Section */}
          <section>
            <h2 className="px-2 pb-3 text-xl font-bold text-text-light-primary dark:text-text-dark-primary">Risk Management</h2>
            <div className="rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
              <div className="flex items-center justify-between border-b border-border-light dark:border-border-dark pb-4">
                <div className="flex items-center gap-3">
                  <div className="flex size-12 items-center justify-center rounded-full bg-success/20">
                    <span className="text-3xl font-bold text-success text-green-500">
                      {riskMetrics ? riskMetrics.riskScore : '0'}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-bold text-text-light-primary dark:text-text-dark-primary">Live Risk Score</h3>
                    <p className="text-sm text-success text-green-500">
                      {riskMetrics ? (riskMetrics.riskScore <= 3 ? 'Low Risk' : riskMetrics.riskScore <= 6 ? 'Medium Risk' : 'High Risk') : 'Unknown'}
                    </p>
                  </div>
                </div>
                <span className="material-symbols-outlined text-text-light-secondary dark:text-text-dark-secondary">more_vert</span>
              </div>
              <div className="grid grid-cols-2 gap-4 pt-4">
                <div className="flex flex-col">
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">Suspicious Activity</p>
                  <p className="text-lg font-semibold text-text-light-primary dark:text-text-dark-primary">
                    {riskMetrics ? riskMetrics.suspiciousActivity : '0'}
                  </p>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">Auto-Reversals</p>
                  <p className="text-lg font-semibold text-text-light-primary dark:text-text-dark-primary">
                    {riskMetrics ? riskMetrics.autoReversals : '0'}
                  </p>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">Velocity Limits</p>
                  <p className="text-lg font-semibold text-text-light-primary dark:text-text-dark-primary">
                    {riskMetrics ? `${riskMetrics.velocityLimitUtilization}% Utilized` : '0% Utilized'}
                  </p>
                </div>
                <div className="flex flex-col">
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">ML Model Status</p>
                  <p className="text-lg font-semibold text-success text-green-500">
                    {riskMetrics ? riskMetrics.mlModelStatus : 'Unknown'}
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Compliance & Security Section */}
          <section>
            <h2 className="px-2 pb-3 text-xl font-bold text-text-light-primary dark:text-text-dark-primary">Compliance & Security</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              {/* SOC2 Badge */}
              <div className="flex flex-col items-center gap-3 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                  <img src={soc2} alt="SOC2" className="w-full h-full" />
                </div>
                <div className="flex flex-col gap-0.5 items-center">
                  <h3 className="text-base font-bold leading-tight text-text-light-primary dark:text-text-dark-primary">SOC2</h3>
                  <p className="text-sm leading-normal text-[#2777EF]">Verified</p>
                </div>
              </div>

              {/* PCI DSS Badge */}
              <div className="flex flex-col items-center gap-3 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                  <img src={pci} alt="PCI DSS" className="w-full h-full" />
                </div>
                <div className="flex flex-col gap-0.5 items-center">
                  <h3 className="text-base font-bold leading-tight text-text-light-primary dark:text-text-dark-primary">PCI DSS</h3>
                  <p className="text-sm leading-normal text-[#2777EF]">Verified</p>
                </div>
              </div>

              {/* ISO Badge */}
              <div className="flex flex-col items-center gap-3 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                  <img src={iso} alt="ISO" className="w-full h-full" />
                </div>
                <div className="flex flex-col gap-0.5 items-center">
                  <h3 className="text-base font-bold leading-tight text-text-light-primary dark:text-text-dark-primary">ISO</h3>
                  <p className="text-sm leading-normal text-[#2777EF]">Verified</p>
                </div>
              </div>

              {/* GDPR Badge */}
              <div className="flex flex-col items-center gap-3 rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <div className="relative w-20 h-20 sm:w-24 sm:h-24">
                  <img src={gdpr} alt="GDPR" className="w-full h-full" />
                </div>
                <div className="flex flex-col gap-0.5 items-center">
                  <h3 className="text-base font-bold leading-tight text-text-light-primary dark:text-text-dark-primary">GDPR</h3>
                  <p className="text-sm leading-normal text-[#2777EF]">Verified</p>
                </div>
              </div>
            </div>
          </section>

          {/* Control Center Section */}
          <section>
            <h2 className="px-2 pb-3 text-xl font-bold text-text-light-primary dark:text-text-dark-primary">Control Center</h2>
            
            {/* Approval Workflows */}
            <div className="rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-text-light-primary dark:text-text-dark-primary">APPROVAL WORKFLOWS</h3>
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">Transaction approval requirements</p>
                </div>
                <button 
                  onClick={handleApprovalWorkflowsToggle}
                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-background-dark ${
                    approvalWorkflowsEnabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-700'
                  }`}
                  role="switch"
                  type="button"
                >
                  <span className={`pointer-events-none inline-block size-5 rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    approvalWorkflowsEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}></span>
                </button>
              </div>
              
              {showApprovalWorkflows && (
                <div className="border-t border-border-light dark:border-border-dark pt-4">
                  <div className="space-y-2 mb-4">
                     <div className="flex items-center justify-between">
                       <span className="text-sm text-text-light-primary dark:text-text-dark-primary">Under $50K:</span>
                       <span className="text-sm font-medium text-success text-green-500">{workflowConfig.under50k}</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-sm text-text-light-primary dark:text-text-dark-primary">$50K-$500K:</span>
                       <span className="text-sm font-medium text-warning text-yellow-500">{workflowConfig.under500k}</span>
                     </div>
                     <div className="flex items-center justify-between">
                       <span className="text-sm text-text-light-primary dark:text-text-dark-primary">Over $500K:</span>
                       <span className="text-sm font-medium text-critical text-red-500">{workflowConfig.over500k}</span>
                     </div>
                  </div>
                   <button 
                     onClick={() => setShowWorkflowDialog(true)}
                     className="text-sm font-medium text-primary hover:text-primary/80 underline"
                   >
                     Configure Workflows
                   </button>
                </div>
              )}
            </div>

            {/* Custom Limits */}
            <div className="rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm mb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-text-light-primary dark:text-text-dark-primary">CUSTOM LIMITS</h3>
                  <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">Transaction and velocity limits</p>
                </div>
                 <button 
                   onClick={() => setShowLimitsDialog(true)}
                   className="text-sm font-medium text-primary hover:text-primary/80"
                 >
                   Adjust Limits
                 </button>
              </div>
              
               <div className="space-y-2">
                 <div className="flex items-center justify-between">
                   <span className="text-sm text-text-light-primary dark:text-text-dark-primary">Daily:</span>
                   <span className="text-sm font-medium text-text-light-primary dark:text-text-dark-primary">{formatCurrency(customLimits.daily)}</span>
                 </div>
                 <div className="flex items-center justify-between">
                   <span className="text-sm text-text-light-primary dark:text-text-dark-primary">Per Transaction:</span>
                   <span className="text-sm font-medium text-text-light-primary dark:text-text-dark-primary">{formatCurrency(customLimits.perTransaction)}</span>
                 </div>
                 <div className="flex items-center justify-between">
                   <span className="text-sm text-text-light-primary dark:text-text-dark-primary">Velocity:</span>
                   <span className="text-sm font-medium text-text-light-primary dark:text-text-dark-primary">{customLimits.velocity}/hour</span>
                 </div>
               </div>
            </div>

            {/* Emergency Controls */}
            <div className="rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
              <h3 className="font-semibold text-text-light-primary dark:text-text-dark-primary mb-4">EMERGENCY CONTROLS</h3>
              <div className="space-y-3">
                <button 
                  onClick={handlePauseSettlements}
                  className={`flex w-full items-center justify-center gap-2 rounded-lg p-3 text-base font-bold text-white transition-colors ${
                    isPaused 
                      ? 'bg-success hover:bg-success/90' 
                      : 'bg-critical hover:bg-critical/90'
                  }`}
                >
                  <span className="material-symbols-outlined">
                    {isPaused ? 'play_circle' : 'pause_circle'}
                  </span>
                  {isPaused ? 'RESUME ALL SETTLEMENTS' : 'PAUSE ALL SETTLEMENTS'}
                </button>
              </div>
            </div>
          </section>

          {/* API Integration Metrics Section */}
          <section>
            <h2 className="px-2 pb-3 text-xl font-bold text-text-light-primary dark:text-text-dark-primary">API Integration Metrics</h2>
            
            {/* Key Performance Indicators */}
            <div className="flex flex-col divide-y divide-border-light dark:divide-border-dark rounded-xl bg-card-light dark:bg-card-dark shadow-sm mb-4">
              <div className="flex items-center justify-between p-4">
                <p className="font-medium text-text-light-secondary dark:text-text-dark-secondary">Your API Calls (24hr)</p>
                <p className="font-semibold text-text-light-primary dark:text-text-dark-primary">
                  {apiMetrics ? `${formatNumber(apiMetrics.callCount)} (live)` : '0 (live)'}
                </p>
              </div>
              <div className="flex items-center justify-between p-4">
                <p className="font-medium text-text-light-secondary dark:text-text-dark-secondary">Average Response Time</p>
                <p className="font-semibold text-text-light-primary dark:text-text-dark-primary">
                  {apiMetrics ? `${apiMetrics.avgResponseTime}ms (live)` : '0ms (live)'}
                </p>
              </div>
              <div className="flex flex-col p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-text-light-secondary dark:text-text-dark-secondary">Uptime This Month</p>
                  <p className="font-semibold text-success text-green-500">
                    {apiMetrics ? `${apiMetrics.uptime}%` : '0%'}
                  </p>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div 
                    className="bg-success h-2 rounded-full transition-all" 
                    style={{ width: `${apiMetrics ? apiMetrics.uptime : 0}%` }}
                  ></div>
                </div>
              </div>
            </div>

            {/* Endpoints Used and Webhooks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Endpoints Used */}
              <div className="rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <h3 className="font-semibold text-text-light-primary dark:text-text-dark-primary mb-3">ENDPOINTS USED:</h3>
                <div className="space-y-2">
                  {apiMetrics?.endpointsUsed?.map((endpoint, index) => (
                    <div key={index} className="flex items-center justify-between border-b border-border-light dark:border-border-dark pb-2 last:border-0 last:pb-0">
                      <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary font-mono">
                        {endpoint.path}:
                      </span>
                      <span className="text-sm font-medium text-text-light-primary dark:text-text-dark-primary">
                        {formatNumber(endpoint.calls)}
                      </span>
                    </div>
                  )) || (
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">No data available</p>
                  )}
                </div>
              </div>

              {/* Webhooks Configured */}
              <div className="rounded-xl bg-card-light dark:bg-card-dark p-4 shadow-sm">
                <h3 className="font-semibold text-text-light-primary dark:text-text-dark-primary mb-3">WEBHOOKS CONFIGURED:</h3>
                <div className="space-y-2">
                  {apiMetrics?.webhooksConfigured?.map((webhook, index) => (
                    <div key={index} className="flex items-center justify-between border-b border-border-light dark:border-border-dark pb-2 last:border-0 last:pb-0">
                      <span className="text-sm text-text-light-secondary dark:text-text-dark-secondary">
                        {webhook.name}
                      </span>
                      <span className={`text-sm font-medium ${webhook.configured ? 'text-success text-green-500' : 'text-gray-500'}`}>
                        {webhook.configured ? '✓' : '✗'}
                      </span>
                    </div>
                  )) || (
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">No data available</p>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              <button 
                onClick={() => window.open(`${getBackendUrl()}/api/docs`, '_blank')}
                className="flex-1 px-4 py-2 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/10 transition-colors"
              >
                View API Docs
              </button>
              <button 
                onClick={featurePopup.showPopup}
                className="flex-1 px-4 py-2 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/10 transition-colors"
              >
                Download SDK
              </button>
            </div>
          </section>

          {/* Compliance Exports Section */}
          <section>
            <h2 className="px-2 pb-3 text-xl font-bold text-text-light-primary dark:text-text-dark-primary">Compliance Exports</h2>
            <div className="flex flex-col divide-y divide-border-light dark:divide-border-dark rounded-xl bg-card-light dark:bg-card-dark shadow-sm">
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined text-text-light-secondary dark:text-text-dark-secondary">description</span>
                  <div>
                    <h3 className="font-semibold text-text-light-primary dark:text-text-dark-primary">Quarterly Audit Trail</h3>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">PDF, CSV</p>
                  </div>
                </div>
                <button className="p-2 text-primary">
                  <span className="material-symbols-outlined">download</span>
                </button>
              </div>
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined text-text-light-secondary dark:text-text-dark-secondary">description</span>
                  <div>
                    <h3 className="font-semibold text-text-light-primary dark:text-text-dark-primary">AML Report</h3>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">CSV</p>
                  </div>
                </div>
                <button className="p-2 text-primary">
                  <span className="material-symbols-outlined">schedule</span>
                </button>
              </div>
              <div className="flex items-center justify-between p-4">
                <div className="flex items-center gap-4">
                  <span className="material-symbols-outlined text-text-light-secondary dark:text-text-dark-secondary">description</span>
                  <div>
                    <h3 className="font-semibold text-text-light-primary dark:text-text-dark-primary">Transaction History</h3>
                    <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary">PDF, CSV</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowExportDialog(true)}
                  className="p-2 text-primary hover:bg-primary/10 rounded transition-colors"
                >
                  <span className="material-symbols-outlined">download</span>
                </button>
              </div>
            </div>
          </section>
         </div>
       </main>

       {/* Configure Workflows Dialog */}
       {showWorkflowDialog && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
             <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Configure Approval Workflows</h3>
             
             <div className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Under $50K
                 </label>
                 <select
                   value={workflowConfig.under50k}
                   onChange={(e) => setWorkflowConfig(prev => ({ ...prev, under50k: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                 >
                   <option value="Auto-approve">Auto-approve</option>
                   <option value="1 approval">1 approval</option>
                   <option value="2 approvals">2 approvals</option>
                 </select>
               </div>
               
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   $50K - $500K
                 </label>
                 <select
                   value={workflowConfig.under500k}
                   onChange={(e) => setWorkflowConfig(prev => ({ ...prev, under500k: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                 >
                   <option value="Auto-approve">Auto-approve</option>
                   <option value="1 approval">1 approval</option>
                   <option value="2 approvals">2 approvals</option>
                 </select>
               </div>
               
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Over $500K
                 </label>
                 <select
                   value={workflowConfig.over500k}
                   onChange={(e) => setWorkflowConfig(prev => ({ ...prev, over500k: e.target.value }))}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                 >
                   <option value="Auto-approve">Auto-approve</option>
                   <option value="1 approval">1 approval</option>
                   <option value="2 approvals">2 approvals</option>
                 </select>
               </div>
             </div>
             
             <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 sm:space-x-0 mt-6">
               <button
                 onClick={() => setShowWorkflowDialog(false)}
                 className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
               >
                 Cancel
               </button>
               <button
                 onClick={handleSaveWorkflowConfig}
                 className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90"
               >
                 Save Changes
               </button>
             </div>
           </div>
         </div>
       )}

       {/* Adjust Limits Dialog */}
       {showLimitsDialog && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
           <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
             <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Adjust Custom Limits</h3>
             
             <div className="space-y-4">
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Daily Limit ($)
                 </label>
                 <input
                   type="number"
                   value={customLimits.daily / 100}
                   onChange={(e) => setCustomLimits(prev => ({ ...prev, daily: Number(e.target.value) * 100 }))}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                   placeholder="10000000"
                 />
               </div>
               
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Per Transaction Limit ($)
                 </label>
                 <input
                   type="number"
                   value={customLimits.perTransaction / 100}
                   onChange={(e) => setCustomLimits(prev => ({ ...prev, perTransaction: Number(e.target.value) * 100 }))}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                   placeholder="5000000"
                 />
               </div>
               
               <div>
                 <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                   Velocity Limit (per hour)
                 </label>
                 <input
                   type="number"
                   value={customLimits.velocity}
                   onChange={(e) => setCustomLimits(prev => ({ ...prev, velocity: Number(e.target.value) }))}
                   className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                   placeholder="100"
                 />
               </div>
             </div>
             
             <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 sm:space-x-0 mt-6">
               <button
                 onClick={() => setShowLimitsDialog(false)}
                 className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
               >
                 Cancel
               </button>
               <button
                 onClick={handleSaveLimitsConfig}
                 className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90"
               >
                 Save Changes
               </button>
             </div>
          </div>
        </div>
      )}

      {/* Transaction History Export Dialog */}
      {showExportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Export Transaction History</h3>
            
            <div className="space-y-4">
              {/* Start Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={exportForm.startDate}
                    onChange={(e) => setExportForm(prev => ({ ...prev, startDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={exportForm.startTime}
                    onChange={(e) => setExportForm(prev => ({ ...prev, startTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* End Date and Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={exportForm.endDate}
                    onChange={(e) => setExportForm(prev => ({ ...prev, endDate: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={exportForm.endTime}
                    onChange={(e) => setExportForm(prev => ({ ...prev, endTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              </div>

              {/* Export Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Export Type
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="pdf"
                      checked={exportForm.exportType === 'pdf'}
                      onChange={(e) => setExportForm(prev => ({ ...prev, exportType: e.target.value as 'pdf' | 'csv' }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">PDF</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="csv"
                      checked={exportForm.exportType === 'csv'}
                      onChange={(e) => setExportForm(prev => ({ ...prev, exportType: e.target.value as 'pdf' | 'csv' }))}
                      className="mr-2"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">CSV</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 sm:space-x-0 mt-6">
              <button
                onClick={() => setShowExportDialog(false)}
                disabled={isExporting}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleExportTransactionHistory}
                disabled={isExporting}
                className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center sm:justify-start gap-2"
              >
                {isExporting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined text-sm">download</span>
                    Download
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Feature Not Available Popup */}
      <FeatureNotAvailablePopup
        isOpen={featurePopup.isOpen}
        onClose={featurePopup.hidePopup}
        position={featurePopup.position}
      />
    </div>
  )
}
 
 export default AdminAnalytics
